import { useState } from 'react';
import { Copy, KeyRound, Plus, Webhook as WebhookIcon } from 'lucide-react';
import {
  API_SCOPE_AREAS, API_SCOPE_LABELS, WEBHOOK_EVENTS, WEBHOOK_EVENT_TYPES,
  type ApiKey, type ApiScopeArea, type Webhook,
} from '@unitly/contracts';
import {
  useApiKeys, useCreateApiKey, useCreateWebhook, useDeleteWebhook, useRevokeApiKey, useRotateWebhookSecret,
  useTestWebhook, useUpdateWebhook, useWebhookDeliveries, useWebhooks,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { ApiError } from '../../api/client.ts';
import { relativeTime } from '../../lib/dates.ts';
import { Sheet } from '../../ui/Sheet.tsx';
import { PageLoader, SelectField, TextField } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';

type Access = 'none' | 'read' | 'write';

function useCopy() {
  const toast = useToast();
  return async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast(`${what} copiato`); } catch { toast('Copia non riuscita: seleziona il testo a mano'); }
  };
}

/** A secret shown once: monospace, selectable, with a copy button. */
function OneTimeSecret({ value, label }: { value: string; label: string }) {
  const copy = useCopy();
  return (
    <div className="secret-box">
      <code>{value}</code>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(value, label)}><Copy size={15} /> Copia</button>
    </div>
  );
}

/** "adesso", "5 min fa", "ieri", "il 3 nov". */
function ago(iso: string) {
  const rel = relativeTime(iso);
  return rel === 'ora' ? 'adesso' : /min|h$/.test(rel) ? `${rel} fa` : rel === 'ieri' ? 'ieri' : `il ${rel}`;
}
const at = (iso: string) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

function scopeSummary(scopes: string[]) {
  const writes = scopes.filter((s) => s.endsWith(':write')).length;
  if (writes === API_SCOPE_AREAS.length) return 'Accesso completo';
  return scopes.map((s) => {
    const [area, level] = s.split(':') as [ApiScopeArea, string];
    return `${API_SCOPE_LABELS[area] ?? area}${level === 'read' ? ' (lettura)' : ''}`;
  }).join(' · ');
}

export function DeveloperSettings() {
  const { orgId } = useOrgCtx();
  const copy = useCopy();
  return (
    <div className="settings-cols">
      <div>
        <div className="card card-pad form">
          <div style={{ fontWeight: 600, fontSize: 17 }}>Collega Unitly ad altri servizi</div>
          <p className="secondary" style={{ margin: 0 }}>
            Con una chiave API un altro servizio (per esempio Globy) legge e modifica i dati di Unitly; con un webhook
            riceve un avviso ogni volta che qualcosa cambia. Le chiavi agiscono con i permessi dell’amministratore che le crea, solo nelle aree scelte.
          </p>
          <div className="secret-box">
            <span className="secondary">ID organizzazione</span>
            <code>{orgId}</code>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => copy(orgId, 'ID')}><Copy size={15} /> Copia</button>
          </div>
          <p className="hint" style={{ margin: 0 }}>
            Guida e riferimento completo: <a href="/sviluppatori" target="_blank" rel="noopener">unitly.it/sviluppatori</a> · specifica
            {' '}<a href="/openapi.json" target="_blank" rel="noopener">OpenAPI</a> (importabile in Postman).
          </p>
        </div>
        <ApiKeys />
      </div>
      <div><Webhooks /></div>
    </div>
  );
}

// ------------------------------------------------------------------ API keys
function ApiKeys() {
  const { orgId } = useOrgCtx();
  const list = useApiKeys(orgId);
  const revoke = useRevokeApiKey(orgId);
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const active = list.data?.filter((k) => !k.revokedAt) ?? [];
  const revoked = list.data?.filter((k) => k.revokedAt) ?? [];

  return (
    <>
      <div className="subhead-row">
        <h3 className="subhead">Chiavi API</h3>
        <button type="button" className="btn btn-plain btn-sm" onClick={() => setCreating(true)}><Plus size={16} /> Nuova chiave</button>
      </div>
      {list.isLoading && <PageLoader />}
      {list.data && !active.length && <p className="hint">Nessuna chiave attiva.</p>}
      {active.length > 0 && (
        <div className="card list">
          {active.map((k) => (
            <div className="list-row" key={k.id}>
              <span className="list-icon" style={{ background: 'var(--fill)', color: 'var(--label-2)' }}><KeyRound size={17} /></span>
              <span className="grow" style={{ minWidth: 0 }}>
                <div>{k.name} <code className="secondary">{k.prefix}…</code></div>
                <div className="secondary">{scopeSummary(k.scopes)}</div>
                <div className="secondary">{keyUsage(k)}</div>
              </span>
              <button type="button" className="btn btn-danger btn-sm" disabled={revoke.isPending}
                onClick={() => confirm(`Revocare la chiave “${k.name}”? I servizi che la usano smetteranno subito di funzionare.`) &&
                  revoke.mutate(k.id, { onSuccess: () => toast('Chiave revocata') })}>
                Revoca
              </button>
            </div>
          ))}
        </div>
      )}
      {revoked.length > 0 && <p className="hint" style={{ marginTop: 8 }}>{revoked.length === 1 ? '1 chiave revocata' : `${revoked.length} chiavi revocate`}.</p>}
      <NewKeySheet open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function keyUsage(k: ApiKey) {
  const parts = [`Creata da ${k.createdBy.displayName}`, k.lastUsedAt ? `usata ${ago(k.lastUsedAt)}` : 'mai usata'];
  if (k.expiresAt) parts.push(`scade il ${new Date(k.expiresAt).toLocaleDateString('it-IT')}`);
  return parts.join(' · ');
}

function NewKeySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const create = useCreateApiKey(orgId);
  const [name, setName] = useState('');
  const [access, setAccess] = useState<Record<ApiScopeArea, Access>>(() => Object.fromEntries(API_SCOPE_AREAS.map((a) => [a, 'none'])) as Record<ApiScopeArea, Access>);
  const [expires, setExpires] = useState('');
  const [key, setKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scopes = API_SCOPE_AREAS.filter((a) => access[a] !== 'none').map((a) => `${a}:${access[a]}`);
  const setAll = (level: Access) => setAccess(Object.fromEntries(API_SCOPE_AREAS.map((a) => [a, level])) as Record<ApiScopeArea, Access>);
  const close = () => {
    setName(''); setAll('none'); setExpires(''); setKey(null); setError(null); create.reset();
    onClose();
  };
  const submit = () => {
    setError(null);
    create.mutate({ name, scopes, expiresInDays: expires ? Number(expires) : null }, {
      onSuccess: (r) => setKey(r.key),
      onError: (e) => setError(e instanceof ApiError ? Object.values(e.errors)[0] ?? e.message : 'Creazione non riuscita'),
    });
  };

  return (
    <Sheet open={open} onClose={close} title={key ? 'Chiave creata' : 'Nuova chiave API'}
      footer={key
        ? <button type="button" className="btn btn-primary" onClick={close}>Ho copiato la chiave</button>
        : <button type="button" className="btn btn-primary" disabled={!name.trim() || !scopes.length || create.isPending} onClick={submit}>Crea chiave</button>}>
      {key ? (
        <div className="form">
          <p style={{ margin: 0 }}>Copia la chiave e salvala nel servizio che la userà: <strong>per sicurezza non la mostreremo più</strong>. Se la perdi, revocala e creane una nuova.</p>
          <OneTimeSecret value={key} label="Chiave" />
          <p className="hint" style={{ margin: 0 }}>Si usa nell’intestazione <code>Authorization: Bearer …</code> di ogni richiesta.</p>
        </div>
      ) : (
        <div className="form">
          <TextField label="Nome" placeholder="Es. Globy" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-autofocus />
          <div>
            <div className="subhead-row" style={{ marginTop: 0 }}>
              <span style={{ fontWeight: 600 }}>Cosa può fare</span>
              <span>
                <button type="button" className="btn btn-plain btn-sm" onClick={() => setAll('read')}>Tutto in lettura</button>
                <button type="button" className="btn btn-plain btn-sm" onClick={() => setAll('write')}>Accesso completo</button>
              </span>
            </div>
            <div className="card list">
              {API_SCOPE_AREAS.map((a) => (
                <div className="list-row" key={a}>
                  <span className="grow">{API_SCOPE_LABELS[a]}</span>
                  <select className="select select-sm" aria-label={API_SCOPE_LABELS[a]} value={access[a]} onChange={(e) => setAccess({ ...access, [a]: e.target.value as Access })}>
                    <option value="none">Nessun accesso</option>
                    <option value="read">Lettura</option>
                    <option value="write">Lettura e scrittura</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
          <SelectField label="Scadenza" value={expires} onChange={(e) => setExpires(e.target.value)}>
            <option value="">Nessuna scadenza</option>
            <option value="30">Tra 30 giorni</option>
            <option value="90">Tra 90 giorni</option>
            <option value="365">Tra un anno</option>
          </SelectField>
          <p className="hint" style={{ margin: 0 }}>
            La chiave agisce con i tuoi permessi, solo nelle aree scelte. Non può gestire chiavi o webhook né accedere al tuo account.
            Smette di funzionare se la revochi o se non sei più amministratore.
          </p>
          {error && <div className="form-error">{error}</div>}
        </div>
      )}
    </Sheet>
  );
}

// ------------------------------------------------------------------ webhooks
const STATUS_LABEL = { delivered: 'Consegnato', pending: 'In attesa', failed: 'Non consegnato' } as const;
const STATUS_CAT = { delivered: 'done', pending: 'review', failed: 'canceled' } as const;

function Webhooks() {
  const { orgId } = useOrgCtx();
  const list = useWebhooks(orgId);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const open = list.data?.find((w) => w.id === openId) ?? null;

  return (
    <>
      <div className="subhead-row">
        <h3 className="subhead">Webhook</h3>
        <button type="button" className="btn btn-plain btn-sm" onClick={() => setCreating(true)}><Plus size={16} /> Nuovo webhook</button>
      </div>
      {list.isLoading && <PageLoader />}
      {list.data && !list.data.length && <p className="hint">Nessun webhook. Un webhook avvisa un altro servizio, con una richiesta firmata, quando in Unitly qualcosa cambia.</p>}
      {list.data && list.data.length > 0 && (
        <div className="card list">
          {list.data.map((w) => (
            <button type="button" className="list-row" key={w.id} onClick={() => setOpenId(w.id)}>
              <span className="list-icon" style={{ background: w.active ? 'var(--accent)' : 'var(--fill)', color: w.active ? '#fff' : 'var(--label-3)' }}><WebhookIcon size={17} /></span>
              <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
                <div className="ellipsis">{w.description || w.url}</div>
                <div className="secondary ellipsis">{w.description ? `${w.url} · ` : ''}{eventsSummary(w.events)}</div>
                <div className="secondary">
                  {!w.active ? (w.disabledReason ?? 'Disattivato') : w.lastDelivery
                    ? `${STATUS_LABEL[w.lastDelivery.status]}${w.lastDelivery.statusCode ? ` (${w.lastDelivery.statusCode})` : ''} ${ago(w.lastDelivery.at)}`
                    : 'Ancora nessun evento'}
                </div>
              </span>
            </button>
          ))}
        </div>
      )}
      <NewWebhookSheet open={creating} onClose={() => setCreating(false)} />
      {open && <WebhookSheet hook={open} onClose={() => setOpenId(null)} />}
    </>
  );
}

const eventsSummary = (events: string[]) => (events.includes('*') ? 'Tutti gli eventi' : events.length === 1 ? WEBHOOK_EVENTS[events[0] as keyof typeof WEBHOOK_EVENTS] ?? events[0] : `${events.length} eventi`);

// Events grouped by their prefix ("task.created" → "task"), in catalog order.
const EVENT_GROUPS = WEBHOOK_EVENT_TYPES.reduce<Record<string, string[]>>((acc, t) => {
  (acc[t.split('.')[0]!] ??= []).push(t);
  return acc;
}, {});

function EventPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const all = value.includes('*');
  const toggle = (t: string) => onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value.filter((x) => x !== '*'), t]);
  return (
    <div className="form">
      <label className="check-row">
        <input type="checkbox" checked={all} onChange={(e) => onChange(e.target.checked ? ['*'] : [])} />
        Tutti gli eventi, anche quelli che aggiungeremo in futuro
      </label>
      {!all && (
        <div className="event-groups">
          {Object.values(EVENT_GROUPS).map((types) => (
            <div key={types[0]} className="wrap-chips">
              {types.map((t) => (
                <button type="button" key={t} className="chip" aria-pressed={value.includes(t)} onClick={() => toggle(t)} title={t}>
                  {WEBHOOK_EVENTS[t as keyof typeof WEBHOOK_EVENTS]}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewWebhookSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const create = useCreateWebhook(orgId);
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<string[]>(['*']);
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setUrl(''); setDescription(''); setEvents(['*']); setSecret(null); setError(null); create.reset();
    onClose();
  };
  const submit = () => {
    setError(null);
    create.mutate({ url: url.trim(), description: description.trim() || null, events: events as CreateEvents }, {
      onSuccess: (r) => setSecret(r.secret),
      onError: (e) => setError(e instanceof ApiError ? Object.values(e.errors)[0] ?? e.message : 'Creazione non riuscita'),
    });
  };
  return (
    <Sheet open={open} onClose={close} title={secret ? 'Webhook creato' : 'Nuovo webhook'}
      footer={secret
        ? <button type="button" className="btn btn-primary" onClick={close}>Ho copiato il segreto</button>
        : <button type="button" className="btn btn-primary" disabled={!url.trim() || !events.length || create.isPending} onClick={submit}>Crea webhook</button>}>
      {secret ? (
        <div className="form">
          <p style={{ margin: 0 }}>Questo è il <strong>segreto di firma</strong>: il servizio che riceve lo usa per verificare che le richieste arrivino davvero da Unitly. Copialo ora: potrai generarne uno nuovo, ma non rivedere questo.</p>
          <OneTimeSecret value={secret} label="Segreto" />
        </div>
      ) : (
        <div className="form">
          <TextField label="Indirizzo che riceve gli eventi" type="url" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} data-autofocus
            hint="Deve essere pubblico e iniziare con https://." />
          <TextField label="Descrizione (facoltativa)" placeholder="Es. Globy – produzione" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
          <div style={{ fontWeight: 600 }}>Eventi</div>
          <EventPicker value={events} onChange={setEvents} />
          {error && <div className="form-error">{error}</div>}
        </div>
      )}
    </Sheet>
  );
}
type CreateEvents = Parameters<ReturnType<typeof useCreateWebhook>['mutate']>[0]['events'];

function WebhookSheet({ hook, onClose }: { hook: Webhook; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const update = useUpdateWebhook(orgId);
  const remove = useDeleteWebhook(orgId);
  const rotate = useRotateWebhookSecret(orgId);
  const test = useTestWebhook(orgId);
  const deliveries = useWebhookDeliveries(orgId, hook.id);
  const toast = useToast();
  const [events, setEvents] = useState<string[]>(hook.events);
  const [secret, setSecret] = useState<string | null>(null);
  const changed = events.slice().sort().join() !== hook.events.slice().sort().join();
  const fail = (e: unknown) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita');

  return (
    <Sheet open onClose={onClose} title={hook.description || 'Webhook'}
      footer={changed
        ? <button type="button" className="btn btn-primary" disabled={!events.length || update.isPending}
            onClick={() => update.mutate({ id: hook.id, events: events as CreateEvents }, { onSuccess: () => toast('Eventi aggiornati'), onError: fail })}>Salva eventi</button>
        : undefined}>
      <div className="form">
        <div className="secondary" style={{ wordBreak: 'break-all' }}>{hook.url}</div>
        {!hook.active && <div className="form-error">{hook.disabledReason ?? 'Disattivato: non riceve eventi.'}</div>}
        <div className="btn-row">
          <button type="button" className="btn btn-secondary btn-sm" disabled={test.isPending || !hook.active}
            onClick={() => test.mutate(hook.id, { onSuccess: () => toast('Evento di prova in invio: l’esito compare qui sotto tra pochi secondi'), onError: fail })}>Invia una prova</button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={update.isPending}
            onClick={() => update.mutate({ id: hook.id, active: !hook.active }, { onSuccess: () => toast(hook.active ? 'Webhook disattivato' : 'Webhook riattivato'), onError: fail })}>
            {hook.active ? 'Disattiva' : 'Riattiva'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={rotate.isPending}
            onClick={() => confirm('Generare un nuovo segreto? Quello attuale smette subito di valere: andrà aggiornato nel servizio che riceve.') &&
              rotate.mutate(hook.id, { onSuccess: (r) => setSecret(r.secret), onError: fail })}>Nuovo segreto</button>
          <button type="button" className="btn btn-danger btn-sm" disabled={remove.isPending}
            onClick={() => confirm('Eliminare il webhook? Il servizio non riceverà più eventi.') &&
              remove.mutate(hook.id, { onSuccess: () => { toast('Webhook eliminato'); onClose(); }, onError: fail })}>Elimina</button>
        </div>
        {secret && (
          <>
            <p style={{ margin: 0 }}>Nuovo segreto di firma: copialo ora, non lo mostreremo più.</p>
            <OneTimeSecret value={secret} label="Segreto" />
          </>
        )}
        <div style={{ fontWeight: 600 }}>Eventi</div>
        <EventPicker value={events} onChange={setEvents} />
        <div style={{ fontWeight: 600 }}>Ultime consegne</div>
        {deliveries.isLoading && <PageLoader />}
        {deliveries.data && !deliveries.data.length && <p className="hint" style={{ margin: 0 }}>Ancora nessun evento.</p>}
        {deliveries.data && deliveries.data.length > 0 && (
          <div className="card list">
            {deliveries.data.map((d) => (
              <div className="list-row" key={d.id}>
                <span className="grow" style={{ minWidth: 0 }}>
                  <div><code>{d.eventType}</code></div>
                  <div className="secondary">
                    {ago(d.createdAt)} · {d.attempts === 1 ? '1 tentativo' : `${d.attempts} tentativi`}
                    {d.lastError ? ` · ${d.lastError}` : ''}
                    {d.nextAttemptAt && d.attempts > 0 ? ` · nuovo tentativo alle ${at(d.nextAttemptAt)}` : ''}
                  </div>
                </span>
                <span className="pill" data-cat={STATUS_CAT[d.status]}>{STATUS_LABEL[d.status]}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Sheet>
  );
}
